import {
  div,
  h1,
  h2,
  span,
  button,
  a,
  fnstate,
  pathParameters,
  p,
  img,
  figure,
  label
} from '@srfnstack/fntags'
import { CreditButton } from '../components/CreditButton.js'
import { extensionId } from '../state.js'
import { NumberSpinner } from '../components/NumberSpinner.js'
import { validateCredits } from '../utils/credits.js'
import { scrapeEtsy } from '../utils/extension.js'

export const DesignSeedDetail = () => {
  const signalId = pathParameters().id
  const signal = fnstate(null)
  const loading = fnstate(true)
  const batchSize = fnstate(5)
  const multipliers = fnstate({ batch_size: 5 })
  batchSize.subscribe(b => multipliers({ batch_size: b }))

  const fetchSignal = async () => {
    loading(true)
    try {
      const res = await fetch(`/api/design-seeds/${signalId}?_=${Date.now()}`)
      if (res.ok) {
        signal(await res.json())
      }
    } catch (err) {
      console.error('Failed to fetch seed:', err)
    } finally {
      loading(false)
    }
  }

  const scrapeWithExtension = async (s, bSize) => {
    try {
      const response = await scrapeEtsy(extensionId(), {
        designSeedId: s.id,
        url: s.product_link,
        callbackUrl: `${window.location.origin}/api/worker/complete-scrape`,
        batchSize: parseInt(bSize)
      })
      if (response && response.success) {
        await window.alert('Scrape completed successfully!')
        fetchSignal()
      } else {
        await window.alert('Scrape started but failed: ' + (response?.error || 'Unknown error'))
      }
    } catch (err) {
      await window.alert(err.message)
    }
  }

  const reAnalyzing = fnstate(false)

  const reAnalyze = async (bSize) => {
    if (!await validateCredits('ETSY_ANALYSIS', { batch_size: bSize })) return

    const instructions = await window.prompt('Additional instructions for AI analysis (optional):', '', 'Re-Analyze Seed')
    if (instructions === null) return

    reAnalyzing(true)
    try {
      const res = await fetch(`/api/design-seeds/${signalId}/re-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batch_size: parseInt(bSize),
          additional_instructions: instructions
        })
      })
      if (res.ok) {
        await window.alert('Re-analysis triggered successfully.')
        fetchSignal()
      } else {
        const data = await res.json()
        await window.alert('Failed to re-analyze: ' + (data.error || 'Unknown error'))
      }
    } catch (err) {
      console.error('Error re-analyzing:', err)
      await window.alert('Error re-analyzing.')
    } finally {
      reAnalyzing(false)
    }
  }

  fetchSignal()

  return div({ class: 'p-4 md:p-6 flex flex-col min-h-[calc(100vh-64px)]' },
    div({ class: 'flex items-center gap-4 mb-6' },
      button({ class: 'btn btn-circle btn-ghost', onclick: () => window.history.back() }, '←'),
      h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Design Seed Detail')
    ),
    loading.bindAs(l => l
      ? div({ class: 'flex justify-center p-20' }, span({ class: 'loading loading-spinner loading-lg text-primary' }))
      : signal.bindAs(s => {
        if (!s) return div({ class: 'alert alert-error' }, 'Seed not found.')

        return div({ class: 'grid grid-cols-1 lg:grid-cols-3 gap-6' },
          // Left column: Info & Raw Data
          div({ class: 'lg:col-span-2 space-y-6' },
            div({ class: 'card bg-base-200 border border-base-300 shadow-sm' },
              div({ class: 'card-body' },
                div({ class: 'flex justify-between items-start' },
                  h2({ class: 'card-title text-2xl' }, s.product_name || 'Unnamed Seed'),
                  span({ class: `badge ${s.processed ? 'badge-success' : 'badge-warning'}` }, s.processed ? 'Processed' : 'Unprocessed')
                ),
                div({ class: 'flex flex-wrap gap-4 mt-2 text-sm opacity-70' },
                  div(span({ class: 'font-bold mr-1' }, 'Source:'), s.source),
                  div(span({ class: 'font-bold mr-1' }, 'Created:'), new Date(s.created_at).toLocaleString()),
                  div(span({ class: 'font-bold mr-1' }, 'ID:'), s.id),
                  s.scraped_price ? div(span({ class: 'font-bold mr-1 text-success' }, 'Market Price:'), s.scraped_price) : ''
                ),
                div({ class: 'mt-4' },
                  a({
                    href: s.product_link,
                    target: '_blank',
                    class: 'btn btn-primary btn-sm'
                  }, 'View Original Listing ↗')
                )
              )
            ),

            div({ class: 'card bg-base-200 border border-base-300 shadow-sm' },
              div({ class: 'card-body' },
                h2({ class: 'font-bold uppercase text-xs opacity-50 tracking-widest mb-2' }, 'Scraped Tags'),
                s.scraped_tags && s.scraped_tags.length > 0
                  ? div({ class: 'flex flex-wrap gap-2' },
                    s.scraped_tags.map(tag => span({ class: 'badge badge-outline badge-sm' }, tag))
                  )
                  : div({ class: 'bg-base-300/30 p-4 rounded-lg text-center opacity-50 italic text-sm' }, 'No tags scraped yet.')
              )
            ),

            div({ class: 'card bg-base-200 border border-base-300 shadow-sm' },
              div({ class: 'card-body' },
                h2({ class: 'font-bold uppercase text-xs opacity-50 tracking-widest mb-2' }, 'Scraped Content'),
                s.scraped_markdown
                  ? div({ class: 'prose prose-sm max-w-none bg-base-100 p-4 rounded-lg border border-base-300 max-h-96 overflow-y-auto whitespace-pre-wrap font-mono text-[12px]' },
                    s.scraped_markdown
                  )
                  : div({ class: 'bg-base-300/30 p-8 rounded-lg text-center opacity-50 italic' }, 'No scraped content available yet.')
              )
            )
          ),

          // Right column: Image & Actions
          div({ class: 'space-y-6' },
            div({ class: 'card bg-base-200 border border-base-300 shadow-sm overflow-hidden' },
              figure({ class: 'aspect-square bg-base-300' },
                s.scraped_image_path
                  ? img({
                    src: `/api/image?file=${encodeURIComponent(s.scraped_image_path)}`,
                    class: 'object-cover w-full h-full'
                  })
                  : div({ class: 'flex flex-col items-center justify-center h-full opacity-20' },
                    span({ class: 'material-symbols-rounded text-6xl' }, 'image'),
                    span('No Image Scraped')
                  )
              ),
              div({ class: 'card-body' },
                h2({ class: 'card-title text-sm' }, 'Seed Assets'),
                p({ class: 'text-xs opacity-70' }, 'Images harvested during the scraping process.')
              )
            ),

            div({ class: 'card bg-base-200 border border-base-300 shadow-sm' },
              div({ class: 'card-body gap-4' },
                h2({ class: 'font-bold uppercase text-xs opacity-50 tracking-widest' }, 'Actions'),

                div({ class: 'form-control w-full' },
                  label({ class: 'label p-0 mb-1' }, span({ class: 'label-text text-[10px] font-bold uppercase opacity-40' }, 'Images to Generate')),
                  NumberSpinner({ state: batchSize, min: 1, max: 100, class: 'w-full' })
                ),

                s.processed
                  ? CreditButton({
                    actionCode: 'ETSY_ANALYSIS',
                    label: reAnalyzing.bindAs(r => r
                      ? span({ class: 'flex items-center gap-2' }, span({ class: 'loading loading-spinner loading-xs' }), 'Analyzing...')
                      : 'Re-Analyze'
                    ),
                    class: 'btn btn-secondary w-full',
                    disabled: reAnalyzing,
                    onclick: () => reAnalyze(batchSize()),
                    multipliers
                  })
                  : CreditButton({
                    actionCode: 'ETSY_ANALYSIS',
                    label: 'Scrape with Extension',
                    class: 'btn btn-primary w-full',
                    onclick: () => scrapeWithExtension(s, batchSize()),
                    multipliers
                  })
              )
            )
          )
        )
      })
    )
  )
}
